<script setup lang="ts">
import Icon from './Icon.vue';

/**
 * Placeholder for a screen/section with nothing to show yet — no data, or a module that isn't
 * entitled yet (AGENTS.md: never fake numbers/rows). Prefer this over rendering an empty table.
 *
 * `symbol` (Material Symbols ligature name) takes precedence over the legacy `icon` (PrimeIcons
 * class string) when both are given; `icon` is kept only for callers that have not migrated yet.
 */
defineProps<{
  icon?: string;
  symbol?: string;
  title: string;
  description?: string;
}>();
</script>

<template>
  <div
    class="jv-state flex flex-col items-center gap-4 rounded-jv-lg bg-jv-surface-lowest px-6 py-10 text-center"
    data-test="empty-state"
  >
    <span class="grid size-24 place-items-center rounded-full bg-jv-surface-low text-jv-primary">
      <Icon v-if="symbol || !icon" :name="symbol ?? 'inbox'" :size="44" />
      <i v-else :class="icon" class="text-xl" />
    </span>
    <div>
      <p class="m-0 text-lg leading-[26px] font-semibold text-jv-on-surface">{{ title }}</p>
      <p v-if="description" class="m-0 mt-1 max-w-sm text-sm text-jv-on-surface-variant">{{ description }}</p>
    </div>
    <div v-if="$slots.default" class="mt-1">
      <slot />
    </div>
  </div>
</template>
